import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, Trash, MoreVertical } from "lucide-react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css"; // Optional: Add a theme for Flatpickr

const ExpenseList = () => {
  // Dummy data for expenses
  const [expenses, setExpenses] = useState([
    {
      id: 1,
      name: "Electric Bill Payment",
      description: "Monthly electricity bill payment",
      invoiceNumber: "INV-001",
      date: "2023-10-01",
      expenseHead: "Electric Bill",
      amount: 5000,
    },
    {
      id: 2,
      name: "Stationary Purchase",
      description: "Office stationary purchase",
      invoiceNumber: "INV-002",
      date: "2023-10-05",
      expenseHead: "Stationary Bill",
      amount: 1200,
    },
    {
      id: 3,
      name: "Telephone Bill Payment",
      description: "Monthly telephone bill payment",
      invoiceNumber: "INV-003",
      date: "2023-10-10",
      expenseHead: "Telephone Bill",
      amount: 800,
    },
  ]);

  const [totalRecords, setTotalRecords] = useState(expenses.length);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(5);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredExpenses, setFilteredExpenses] = useState(expenses);
  const [dropdownOpen, setDropdownOpen] = useState(null);

  const [formData, setFormData] = useState({
    expenseHead: "",
    name: "",
    invoiceNumber: "",
    amount: "",
    description: "",
    date: "",
    attachedDocument: null,
  });

  const toast = useRef(null);
  const navigate = useNavigate();

  // Toggle dropdown
  const toggleDropdown = (id) => {
    setDropdownOpen(dropdownOpen === id ? null : id);
  };

  // Filter expenses based on search term
  useEffect(() => {
    let filtered = expenses;
    if (searchTerm) {
      filtered = filtered.filter((expense) =>
        expense.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    setFilteredExpenses(filtered);
    setTotalRecords(filtered.length);
  }, [searchTerm, expenses]);

  // Handle pagination
  const onPageChange = (event) => {
    setFirst(event.first);
    setRows(event.rows);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (selectedExpense) {
        // Update expense
        const updatedExpenses = expenses.map((expense) =>
          expense.id === selectedExpense.id ? { ...expense, ...formData } : expense
        );
        setExpenses(updatedExpenses);
        toast.current.show({
          severity: "success",
          summary: "Updated",
          detail: "Expense updated successfully",
          life: 3000,
        });
      } else {
        // Add new expense
        const newExpense = { id: expenses.length + 1, ...formData };
        setExpenses([...expenses, newExpense]);
        setTotalRecords(expenses.length + 1);
        toast.current.show({
          severity: "success",
          summary: "Added",
          detail: "Expense added successfully",
          life: 3000,
        });
      }
      toggle();
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save expense",
        life: 3000,
      });
    }
    setLoading(false);
  };

  // Handle delete
  const handleDelete = async () => {
    if (!expenseToDelete) return;
    setLoading(true);
    try {
      const updatedExpenses = expenses.filter(
        (expense) => expense.id !== expenseToDelete.id
      );
      setExpenses(updatedExpenses);
      setTotalRecords(updatedExpenses.length);
      toast.current.show({
        severity: "success",
        summary: "Deleted",
        detail: "Expense deleted successfully",
        life: 3000,
      });
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete expense",
        life: 3000,
      });
    }
    setExpenseToDelete(null);
    setLoading(false);
    toggleDeleteModal();
  };

  // Toggle modal
  const toggle = () => {
    if (modal) {
      setSelectedExpense(null);
      setFormData({
        expenseHead: "",
        name: "",
        invoiceNumber: "",
        amount: "",
        description: "",
        date: "",
        attachedDocument: null,
      });
    }
    setModal(!modal);
  };

  // Toggle delete modal
  const toggleDeleteModal = () => setDeleteModal(!deleteModal);

  // Breadcrumb items
  const breadcrumbItems = [
    { title: "Finance", link: "/" },
    { title: "Expense List", link: "#" },
  ];

  return (
    <React.Fragment>
        <div className="page-content">
            <Container fluid>
            <Breadcrumbs title="Expense List" breadcrumbItems={breadcrumbItems} />
            <Card>
                <CardHeader className="d-flex justify-content-between align-items-center">
                <span>Expense List</span>
                <div className="d-flex align-items-center gap-3">
                    <Input
                    type="text"
                    placeholder="Search by name"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: "300px" }}
                    />
                    <Button color="primary" onClick={toggle}>
                    <i className="mdi mdi-plus mr-1"></i> Add Expense
                    </Button>
                </div>
                </CardHeader>
                <CardBody>
                <DataTable
                    value={filteredExpenses.slice(first, first + rows)}
                    loading={loading}
                    stripedRows
                >
                    <Column header="SI No" body={(rowData, options) => first + options.rowIndex + 1} />
                    <Column field="name" header="Name" sortable />
                    <Column field="description" header="Description" sortable />
                    <Column field="invoiceNumber" header="Invoice Number" sortable />
                    <Column field="date" header="Date" sortable />
                    <Column field="expenseHead" header="Expense Head" sortable />
                    <Column field="amount" header="Amount" sortable />
                    <Column
                    header="Actions"
                    body={(rowData) => (
                        <Dropdown
                        isOpen={dropdownOpen === rowData.id}
                        toggle={() => toggleDropdown(rowData.id)}
                        >
                        <DropdownToggle tag="button" className="btn btn-light">
                            <MoreVertical size={20} />
                        </DropdownToggle>
                        <DropdownMenu>
                            <DropdownItem
                            onClick={() => {
                                setSelectedExpense(rowData);
                                setFormData({
                                expenseHead: rowData.expenseHead,
                                name: rowData.name,
                                invoiceNumber: rowData.invoiceNumber,
                                amount: rowData.amount,
                                description: rowData.description,
                                date: rowData.date,
                                attachedDocument: null,
                                });
                                toggle();
                            }}
                            >
                            <Edit size={16} className="me-2" />
                            Edit
                            </DropdownItem>
                            <DropdownItem
                            onClick={() => {
                                setExpenseToDelete(rowData);
                                toggleDeleteModal();
                            }}
                            className="text-danger"
                            >
                            <Trash size={16} className="me-2" />
                            Delete
                            </DropdownItem>
                        </DropdownMenu>
                        </Dropdown>
                    )}
                    />
                </DataTable>
                <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalRecords}
                    onPageChange={onPageChange}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                />
                </CardBody>
            </Card>
            </Container>
        </div>

        {/* Add/Edit Expense Modal */}
        <Modal isOpen={modal} toggle={toggle} size="lg" centered>
            <Form onSubmit={handleSubmit}>
            <ModalHeader toggle={toggle}>
                {selectedExpense ? "Edit Expense" : "Add Expense"}
            </ModalHeader>
            <ModalBody>
                <Row>
                    <Col md={6} className="mb-3">
                        <FormGroup>
                            <Label>Expense Head</Label>
                            <div className="d-flex align-items-center gap-3">
                            <Input
                                type="select"
                                value={formData.expenseHead}
                                onChange={(e) =>
                                    setFormData({ ...formData, expenseHead: e.target.value })
                                }
                            >
                                <option value="">Select Expense Head</option>
                                <option value="Electric Bill">Electric Bill</option>
                                <option value="Stationary Bill">Stationary Bill</option>
                                <option value="Telephone Bill">Telephone Bill</option>
                            </Input>
                            <Button
                                color="primary"
                                onClick={() => navigate("/admin/expense-heads")}
                            >
                                Add
                            </Button>
                            </div>
                        </FormGroup>
                    </Col>
                
                    <Col md={6} className="mb-3">
                        <FormGroup>
                        <Label>Name</Label>
                        <Input
                            type="text"
                            value={formData.name}
                            onChange={(e) =>
                            setFormData({ ...formData, name: e.target.value })
                            }
                        />
                        </FormGroup>
                    </Col>
                    <Col md={6} className="mb-3">
                        <FormGroup>
                        <Label>Invoice Number</Label>
                        <Input
                            type="text"
                            value={formData.invoiceNumber}
                            onChange={(e) =>
                            setFormData({ ...formData, invoiceNumber: e.target.value })
                            }
                        />
                        </FormGroup>
                    </Col>
                    <Col md={6} className="mb-3">
                        <FormGroup>
                        <Label>Amount</Label>
                        <Input
                            type="number"
                            value={formData.amount}
                            onChange={(e) =>
                            setFormData({ ...formData, amount: e.target.value })
                            }
                        />
                        </FormGroup>
                    </Col>
                    <Col md={6} className="mb-3">
                        <FormGroup>
                        <Label>Date</Label>
                        <Flatpickr
                            className="form-control"
                            placeholder="Select Date"
                            options={{
                            dateFormat: "Y-m-d", // Format: YYYY-MM-DD
                            altInput: true,
                            altFormat: "F j, Y", // Display format: Month Day, Year
                            }}
                            value={formData.date}
                            onChange={(selectedDates) => {
                            setFormData({ ...formData, date: selectedDates[0].toISOString().split("T")[0] });
                            }}
                        />
                        </FormGroup>
                    </Col>
                    <Col md={6} className="mb-3">
                        <FormGroup>
                        <Label>Attached Document</Label>
                        <Input
                            type="file"
                            onChange={(e) =>
                            setFormData({ ...formData, attachedDocument: e.target.files[0] })
                            }
                        />
                        </FormGroup>
                    </Col>
                    <Col md={12} className="mb-3">
                        <FormGroup>
                        <Label>Description</Label>
                        <Input
                            type="textarea"
                            value={formData.description}
                            onChange={(e) =>
                            setFormData({ ...formData, description: e.target.value })
                            }
                        />
                        </FormGroup>
                    </Col>
                </Row>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                {selectedExpense ? "Update" : "Add"}
                {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
                </Button>
            </ModalFooter>
            </Form>
        </Modal>

      {/* Delete Confirmation Modal */}
        <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
            <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
            <ModalBody>Are you sure you want to delete this expense?</ModalBody>
            <ModalFooter>
            <Button color="secondary" onClick={toggleDeleteModal}>
                Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} disabled={loading}>
                Delete
                {loading && <Spinner className="ms-1" size="sm">Loading...</Spinner>}
            </Button>
            </ModalFooter>
        </Modal>

      <Toast ref={toast} />
    </React.Fragment>
  );
};

export default ExpenseList;